import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Lock,
  Unlock,
  Fingerprint,
  KeyRound,
  Grid3X3,
  Sliders,
  Clock,
  CheckCircle2,
  AlertCircle,
  X,
  Sparkles,
  RefreshCw,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  Search,
} from 'lucide-react';
import { useApplications, APP_ICON_MAP, APP_COLOR_MAP } from '../../hooks/useApplications';
import { useAuth } from '../../contexts/AuthContext';
import appSecurityService, { ALL_CIPHERVAULT_APPS } from '../../security/AppSecurityService';
import { hashCredential, generateSalt } from '../../security/AppSecurityCrypto';
import PinKeypad from './PinKeypad';
import PatternLockCanvas from './PatternLockCanvas';

export default function ApplicationSecuritySettings() {
  const { applications, loading: appsLoading } = useApplications();
  const { user, unlockVaultWithBiometrics, reauthenticateWithMasterPassword } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // App Security config state
  const [localConfigs, setLocalConfigs] = useState({});

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Reload config for user
  const loadConfigs = () => {
    if (!user?.id) return;
    const cfg = appSecurityService.getSecurityConfig(user.id);
    setLocalConfigs(cfg.apps || {});
  };

  useEffect(() => {
    loadConfigs();
    const handleUpdate = () => loadConfigs();
    window.addEventListener('app-security-updated', handleUpdate);
    return () => window.removeEventListener('app-security-updated', handleUpdate);
  }, [user?.id]);

  // Merge loaded apps with canonical ALL_CIPHERVAULT_APPS list
  const fullAppList = useMemo(() => {
    const map = new Map();
    // Default apps first
    for (const app of ALL_CIPHERVAULT_APPS) {
      map.set(app.slug, {
        slug: app.slug,
        name: app.name,
        category: app.defaultCategory,
        enabled: true,
      });
    }
    // Overlay dynamic applications from backend if available
    for (const app of applications) {
      if (app.slug) {
        map.set(app.slug, {
          ...map.get(app.slug),
          ...app,
        });
      }
    }
    return Array.from(map.values());
  }, [applications]);

  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return fullAppList;
    const q = searchQuery.toLowerCase();
    return fullAppList.filter(
      (a) => a.name.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q) || a.category?.toLowerCase().includes(q)
    );
  }, [fullAppList, searchQuery]);

  // Handle direct ON/OFF toggle switch
  const handleToggleProtection = (app, e) => {
    e?.stopPropagation();
    const current = localConfigs[app.slug] || { enabled: false };
    const nextEnabled = !current.enabled;

    // If turning ON and has never been configured with any methods, open config modal
    if (nextEnabled && (!current.methods || (!current.methods.biometric && !current.methods.password && !current.methods.pin && !current.methods.pattern))) {
      openConfigModal(app, {
        ...current,
        enabled: true,
        primaryMethod: 'biometric',
        methods: { biometric: true, password: true, pin: false, pattern: false },
        autoLockTimeout: 5,
      });
      return;
    }

    const updatedApp = {
      ...current,
      enabled: nextEnabled,
      primaryMethod: current.primaryMethod || 'biometric',
      methods: current.methods || { biometric: true, password: true, pin: false, pattern: false },
      autoLockTimeout: current.autoLockTimeout !== undefined ? current.autoLockTimeout : 5,
    };

    appSecurityService.saveAppConfig(user?.id, app.slug, updatedApp);
    setLocalConfigs((prev) => ({ ...prev, [app.slug]: updatedApp }));
    showToast(`${app.name} security lock ${nextEnabled ? 'enabled' : 'disabled'}.`);
  };

  // Modal editing state
  const [modalState, setModalState] = useState({
    enabled: false,
    primaryMethod: 'biometric',
    methods: { biometric: true, password: true, pin: false, pattern: false },
    autoLockTimeout: 5,
    pinConfig: null,
    patternConfig: null,
  });

  // Sub-modal states for setting PIN or Pattern
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [pinSetupStep, setPinSetupStep] = useState(1); // 1 = enter, 2 = confirm
  const [tempPin, setTempPin] = useState('');
  const [pinError, setPinError] = useState('');

  const [patternSetupOpen, setPatternSetupOpen] = useState(false);
  const [patternSetupStep, setPatternSetupStep] = useState(1);
  const [tempPattern, setTempPattern] = useState(null);
  const [patternError, setPatternError] = useState('');

  // Lock body scroll and notify navigation to hide floating bottom menu when drawer/modal is open
  useEffect(() => {
    const isAnyActive = configModalOpen || pinSetupOpen || patternSetupOpen;
    if (isAnyActive) {
      document.body.classList.add('mobile-modal-open');
      document.body.classList.add('card-drawer-open');
      document.body.classList.add('app-sec-drawer-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
      document.body.classList.remove('card-drawer-open');
      document.body.classList.remove('app-sec-drawer-open');
    }
    return () => {
      document.body.classList.remove('mobile-modal-open');
      document.body.classList.remove('card-drawer-open');
      document.body.classList.remove('app-sec-drawer-open');
    };
  }, [configModalOpen, pinSetupOpen, patternSetupOpen]);

  const openConfigModal = (app, initialOverride) => {
    setSelectedApp(app);
    const existing = initialOverride || localConfigs[app.slug] || appSecurityService.getAppConfig(user?.id, app.slug);
    setModalState({
      enabled: existing.enabled ?? true,
      primaryMethod: existing.primaryMethod || 'biometric',
      methods: {
        biometric: existing.methods?.biometric ?? true,
        password: existing.methods?.password ?? true,
        pin: Boolean(existing.methods?.pin && existing.pinConfig),
        pattern: Boolean(existing.methods?.pattern && existing.patternConfig),
      },
      autoLockTimeout: existing.autoLockTimeout !== undefined ? existing.autoLockTimeout : 5,
      pinConfig: existing.pinConfig || null,
      patternConfig: existing.patternConfig || null,
    });
    setConfigModalOpen(true);
  };

  // Handle PIN Setup completion
  const handlePinInput = (entered) => {
    if (pinSetupStep === 1) {
      setTempPin(entered);
      setPinSetupStep(2);
      setPinError('');
    } else {
      if (entered === tempPin) {
        // Hash PIN securely with PBKDF2/SHA-256 and unique 16-byte random salt
        const salt = generateSalt(16);
        hashCredential(entered, salt).then((hash) => {
          setModalState((prev) => ({
            ...prev,
            pinConfig: { salt, hash, length: entered.length },
            methods: { ...prev.methods, pin: true },
          }));
          setPinSetupOpen(false);
          setPinSetupStep(1);
          setTempPin('');
          setPinError('');
          showToast('Numeric PIN configured securely!');
        });
      } else {
        setPinError('PINs do not match. Please try again.');
        setTimeout(() => {
          setPinSetupStep(1);
          setTempPin('');
          setPinError('');
        }, 1200);
      }
    }
  };

  // Handle Pattern Setup completion
  const handlePatternInput = (nodes) => {
    if (nodes.length < 4) {
      setPatternError('Please connect at least 4 dots.');
      return;
    }
    setPatternError('');

    if (patternSetupStep === 1) {
      setTempPattern(nodes);
      setPatternSetupStep(2);
    } else {
      const match = nodes.length === tempPattern.length && nodes.every((val, idx) => val === tempPattern[idx]);
      if (match) {
        const salt = generateSalt(16);
        hashCredential(nodes, salt).then((hash) => {
          setModalState((prev) => ({
            ...prev,
            patternConfig: { salt, hash, nodeCount: nodes.length },
            methods: { ...prev.methods, pattern: true },
          }));
          setPatternSetupOpen(false);
          setPatternSetupStep(1);
          setTempPattern(null);
          setPatternError('');
          showToast('Pattern lock configured securely!');
        });
      } else {
        setPatternError('Pattern did not match. Please try again.');
        setTimeout(() => {
          setPatternSetupStep(1);
          setTempPattern(null);
          setPatternError('');
        }, 1200);
      }
    }
  };

  // Save Modal Configuration
  const handleSaveModalConfig = () => {
    if (!selectedApp) return;

    // Validate that at least one method is selected if enabled
    if (modalState.enabled) {
      const hasAnyMethod =
        modalState.methods.biometric ||
        modalState.methods.password ||
        (modalState.methods.pin && modalState.pinConfig) ||
        (modalState.methods.pattern && modalState.patternConfig);

      if (!hasAnyMethod) {
        showToast('Please enable at least one authentication method.', 'error');
        return;
      }
    }

    // Ensure primary method is one of the enabled methods
    let primary = modalState.primaryMethod;
    if (primary === 'pin' && (!modalState.methods.pin || !modalState.pinConfig)) primary = 'password';
    if (primary === 'pattern' && (!modalState.methods.pattern || !modalState.patternConfig)) primary = 'password';
    if (primary === 'biometric' && !modalState.methods.biometric) primary = 'password';

    const finalConfig = {
      enabled: modalState.enabled,
      primaryMethod: primary,
      methods: {
        biometric: modalState.methods.biometric,
        password: modalState.methods.password,
        pin: Boolean(modalState.methods.pin && modalState.pinConfig),
        pattern: Boolean(modalState.methods.pattern && modalState.patternConfig),
      },
      pinConfig: modalState.pinConfig,
      patternConfig: modalState.patternConfig,
      passwordConfig: { type: 'master' },
      autoLockTimeout: Number(modalState.autoLockTimeout),
    };

    appSecurityService.saveAppConfig(user?.id, selectedApp.slug, finalConfig);
    setLocalConfigs((prev) => ({ ...prev, [selectedApp.slug]: finalConfig }));
    setConfigModalOpen(false);
    showToast(`Security settings for ${selectedApp.name} saved successfully!`);
  };

  return (
    <div className="app-security-settings-container animate-fade-in">
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`} style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}>
          {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Hero / Header Card */}
      <div className="settings-section-card glass-panel app-sec-hero-card">
        <div className="app-sec-hero-top">
          <div className="app-sec-hero-info">
            <div className="app-sec-hero-icon">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h2 className="app-sec-hero-title">
                Application Security & Per-App Locks
              </h2>
              <p className="app-sec-hero-desc">
                Every application features independent security protection. Default security is OFF. When enabled, authentication is enforced on launch.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="btn-secondary app-sec-hero-btn"
            onClick={() => {
              appSecurityService.lockAllApps();
              showToast('All protected applications have been locked.');
            }}
            title="Immediately lock all apps that have security active"
          >
            <Lock size={14} /> Lock All Protected Apps
          </button>
        </div>

        {/* Search / Filter Bar */}
        <div className="app-sec-search-wrap">
          <Search size={16} className="app-sec-search-icon" />
          <input
            type="text"
            className="input-field app-sec-search-input"
            placeholder="Filter applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Applications Grid / Cards */}
      <div className="app-security-grid">
        {filteredApps.map((app) => {
          const config = localConfigs[app.slug] || { enabled: false };
          const isProtected = Boolean(config.enabled);
          const IconComponent = APP_ICON_MAP[app.slug] || APP_ICON_MAP[app.icon] || Shield;
          const brandColor = APP_COLOR_MAP[app.slug] || '#10b981';

          return (
            <div
              key={app.slug}
              className={`app-security-card glass-panel ${isProtected ? 'is-protected' : ''}`}
            >
              {/* Card Top: Icon, Titles, Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      width: '38px',
                      height: '38px',
                      minWidth: '38px',
                      borderRadius: '10px',
                      background: `${brandColor}18`,
                      border: `1px solid ${brandColor}33`,
                      color: brandColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent size={20} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h3 className="app-security-card-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {app.name}
                    </h3>
                    <span className="app-security-card-sub">
                      {app.category || 'Application'}
                    </span>
                  </div>
                </div>

                {/* Switcher Toggle (Active / Deactive) */}
                <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', margin: 0, flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={isProtected}
                    onChange={(e) => handleToggleProtection(app, e)}
                    style={{ display: 'none' }}
                  />
                  <div
                    style={{
                      width: '42px',
                      height: '24px',
                      borderRadius: '12px',
                      background: isProtected ? '#10b981' : 'rgba(255, 255, 255, 0.22)',
                      position: 'relative',
                      transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      boxShadow: isProtected ? '0 2px 8px rgba(16, 185, 129, 0.35)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: '3px',
                        left: isProtected ? '21px' : '3px',
                        transition: 'left 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                      }}
                    />
                  </div>
                </label>
              </div>

              {/* Status Row & Enabled Methods Badges */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', fontSize: '0.76rem', marginTop: '2px' }}>
                <div>
                  {isProtected ? (
                    <span className="app-security-card-status protected">
                      <ShieldCheck size={13} /> Protected
                    </span>
                  ) : (
                    <span className="app-security-card-status unprotected">
                      <Unlock size={13} /> Protection OFF (Direct Access)
                    </span>
                  )}
                </div>

                {isProtected && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {config.methods?.biometric && (
                      <span title="Biometrics Enabled" style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-primary)' }}>
                        <Fingerprint size={11} />
                      </span>
                    )}
                    {config.methods?.pin && (
                      <span title="PIN Enabled" style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-primary)' }}>
                        PIN
                      </span>
                    )}
                    {config.methods?.pattern && (
                      <span title="Pattern Enabled" style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-primary)' }}>
                        <Grid3X3 size={11} />
                      </span>
                    )}
                    {config.methods?.password && (
                      <span title="Password Fallback Enabled" style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px', color: 'var(--text-primary)' }}>
                        <KeyRound size={11} />
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Bottom Action */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => openConfigModal(app)}
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    padding: '6px 12px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    color: '#10b981',
                    border: '1px solid rgba(16, 185, 129, 0.28)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                  }}
                >
                  <SettingsIcon size={13} />
                  <span>Configure Security</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── APP SECURITY CONFIGURATION RIGHT SIDEBAR DRAWER ─── */}
      {configModalOpen && selectedApp && (
        <div className="card-drawer-wrapper app-sec-drawer-wrapper" role="presentation" data-hide-dock="true">
          <div
            className="card-drawer-overlay app-sec-drawer-overlay animate-fade-in"
            onClick={() => setConfigModalOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="card-drawer app-sec-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedApp.name} Security Settings`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="card-drawer-header app-sec-drawer-header">
              <div className="app-sec-drawer-header-left">
                <div
                  className="app-sec-drawer-icon-badge"
                  style={{
                    background: `${APP_COLOR_MAP[selectedApp.slug] || '#10b981'}20`,
                    color: APP_COLOR_MAP[selectedApp.slug] || '#10b981',
                    border: `1px solid ${APP_COLOR_MAP[selectedApp.slug] || '#10b981'}35`,
                  }}
                >
                  {(() => {
                    const AppIcon = APP_ICON_MAP[selectedApp.slug] || APP_ICON_MAP[selectedApp.icon] || ShieldCheck;
                    return <AppIcon size={22} />;
                  })()}
                </div>
                <div className="app-sec-drawer-titles">
                  <h3 className="app-sec-drawer-title">{selectedApp.name} Security</h3>
                  <p className="app-sec-drawer-subtitle">Configure independent lock & authentication</p>
                </div>
              </div>
              <button
                type="button"
                className="app-sec-drawer-close"
                onClick={() => setConfigModalOpen(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="card-drawer-body app-sec-drawer-body">
              {/* Master App Protection Switch */}
              <div
                className="app-sec-toggle-card"
                style={{
                  background: modalState.enabled ? 'rgba(16, 185, 129, 0.09)' : 'rgba(255,255,255,0.04)',
                  border: modalState.enabled ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: '0.92rem', display: 'block' }}>Require Lock on Open</strong>
                  <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
                    {modalState.enabled ? 'Demands authentication before displaying' : 'Disabled (Opens directly without prompt)'}
                  </span>
                </div>
                <label style={{ cursor: 'pointer', margin: 0, flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={modalState.enabled}
                    onChange={(e) => setModalState((prev) => ({ ...prev, enabled: e.target.checked }))}
                    style={{ display: 'none' }}
                  />
                  <div
                    style={{
                      width: '46px',
                      height: '26px',
                      borderRadius: '13px',
                      background: modalState.enabled ? '#10b981' : 'rgba(255,255,255,0.18)',
                      position: 'relative',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: '3px',
                        left: modalState.enabled ? '23px' : '3px',
                        transition: 'left 0.25s ease',
                      }}
                    />
                  </div>
                </label>
              </div>

              {/* Authentication Methods Section */}
              <div style={{ opacity: modalState.enabled ? 1 : 0.45, pointerEvents: modalState.enabled ? 'auto' : 'none', transition: 'opacity 0.2s' }}>
                <label className="ios-input-label" style={{ marginBottom: '10px', display: 'block' }}>
                  Allowed Authentication Methods
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Biometrics */}
                  <div className="app-sec-method-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Fingerprint size={18} color="#10b981" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="app-sec-method-title" style={{ fontWeight: 600, fontSize: '0.86rem' }}>Biometrics</div>
                        <div className="app-sec-method-desc" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Face ID / Touch ID / Fingerprint / Passkey</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      style={{ width: '18px', height: '18px', accentColor: '#10b981', cursor: 'pointer', flexShrink: 0 }}
                      checked={modalState.methods.biometric}
                      onChange={(e) => setModalState((prev) => ({
                        ...prev,
                        methods: { ...prev.methods, biometric: e.target.checked }
                      }))}
                    />
                  </div>

                  {/* PIN */}
                  <div className="app-sec-method-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#3b82f6' }}>#</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="app-sec-method-title" style={{ fontWeight: 600, fontSize: '0.86rem' }}>Numeric PIN</div>
                        <div className="app-sec-method-desc" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                          {modalState.pinConfig ? `Configured (${modalState.pinConfig.length} digits, Salted PBKDF2)` : 'Not set up'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: '0.72rem', padding: '4px 8px', height: '28px' }}
                        onClick={() => {
                          setPinSetupStep(1);
                          setTempPin('');
                          setPinError('');
                          setPinSetupOpen(true);
                        }}
                      >
                        {modalState.pinConfig ? 'Change PIN' : 'Set PIN'}
                      </button>
                      <input
                        type="checkbox"
                        style={{ width: '18px', height: '18px', accentColor: '#3b82f6', cursor: 'pointer' }}
                        checked={modalState.methods.pin && Boolean(modalState.pinConfig)}
                        disabled={!modalState.pinConfig}
                        onChange={(e) => setModalState((prev) => ({
                          ...prev,
                          methods: { ...prev.methods, pin: e.target.checked }
                        }))}
                      />
                    </div>
                  </div>

                  {/* Pattern */}
                  <div className="app-sec-method-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Grid3X3 size={18} color="#8b5cf6" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="app-sec-method-title" style={{ fontWeight: 600, fontSize: '0.86rem' }}>Pattern Lock</div>
                        <div className="app-sec-method-desc" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                          {modalState.patternConfig ? `Configured (${modalState.patternConfig.nodeCount} dots, Salted PBKDF2)` : 'Not set up'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <button
                        type="button"
                        className="btn-secondary"
                        style={{ fontSize: '0.72rem', padding: '4px 8px', height: '28px' }}
                        onClick={() => {
                          setPatternSetupStep(1);
                          setTempPattern(null);
                          setPatternError('');
                          setPatternSetupOpen(true);
                        }}
                      >
                        {modalState.patternConfig ? 'Change Pattern' : 'Set Pattern'}
                      </button>
                      <input
                        type="checkbox"
                        style={{ width: '18px', height: '18px', accentColor: '#8b5cf6', cursor: 'pointer' }}
                        checked={modalState.methods.pattern && Boolean(modalState.patternConfig)}
                        disabled={!modalState.patternConfig}
                        onChange={(e) => setModalState((prev) => ({
                          ...prev,
                          methods: { ...prev.methods, pattern: e.target.checked }
                        }))}
                      />
                    </div>
                  </div>

                  {/* Password / Master Password */}
                  <div className="app-sec-method-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <KeyRound size={18} color="#f59e0b" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="app-sec-method-title" style={{ fontWeight: 600, fontSize: '0.86rem' }}>Master Password</div>
                        <div className="app-sec-method-desc" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Vault Master Password verification</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      style={{ width: '18px', height: '18px', accentColor: '#f59e0b', cursor: 'pointer', flexShrink: 0 }}
                      checked={modalState.methods.password}
                      onChange={(e) => setModalState((prev) => ({
                        ...prev,
                        methods: { ...prev.methods, password: e.target.checked }
                      }))}
                    />
                  </div>
                </div>

                {/* Primary Authentication Method */}
                <div style={{ marginTop: '16px' }}>
                  <label className="ios-input-label" htmlFor="primary-method-select">Default Primary Unlock Method</label>
                  <select
                    id="primary-method-select"
                    className="input-field"
                    value={modalState.primaryMethod}
                    onChange={(e) => setModalState((prev) => ({ ...prev, primaryMethod: e.target.value }))}
                    style={{ fontSize: '0.85rem' }}
                  >
                    {modalState.methods.biometric && <option value="biometric">Biometrics (Face ID / Fingerprint)</option>}
                    {modalState.methods.pin && modalState.pinConfig && <option value="pin">Numeric PIN</option>}
                    {modalState.methods.pattern && modalState.patternConfig && <option value="pattern">Pattern Lock</option>}
                    {modalState.methods.password && <option value="password">Master Password</option>}
                  </select>
                </div>

                {/* Auto-Lock Inactivity Timeout */}
                <div style={{ marginTop: '16px' }}>
                  <label className="ios-input-label" htmlFor="autolock-select">Auto-Lock Inactivity Timeout</label>
                  <select
                    id="autolock-select"
                    className="input-field"
                    value={modalState.autoLockTimeout}
                    onChange={(e) => setModalState((prev) => ({ ...prev, autoLockTimeout: Number(e.target.value) }))}
                    style={{ fontSize: '0.85rem' }}
                  >
                    <option value="0">Immediate (Lock as soon as app is closed or blurred)</option>
                    <option value="1">1 minute (High Security)</option>
                    <option value="5">5 minutes (Recommended)</option>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="-1">Keep unlocked for browser session</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="card-drawer-footer app-sec-drawer-footer">
              <button
                type="button"
                className="btn-secondary app-sec-btn-cancel"
                onClick={() => setConfigModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary app-sec-btn-save"
                onClick={handleSaveModalConfig}
              >
                <CheckCircle2 size={16} />
                <span>Save Security Settings</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ─── SUB-MODAL: PIN SETUP ─── */}
      {pinSetupOpen && (
        <div className="card-drawer-wrapper" role="presentation" data-hide-dock="true" style={{ zIndex: 100100 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setPinSetupOpen(false)} aria-hidden="true" />
          <div
            className="modal-card glass-panel animate-scale-up"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '340px', width: '90%', margin: 'auto', borderRadius: '20px', padding: '24px', textAlign: 'center', zIndex: 100102 }}
          >
            <PinKeypad
              pinLength={4}
              title={pinSetupStep === 1 ? 'Create 4-Digit PIN' : 'Confirm Your PIN'}
              subtitle={pinSetupStep === 1 ? 'Choose a memorable numeric passkey' : 'Re-enter the same PIN to verify'}
              error={pinError}
              onComplete={handlePinInput}
            />
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="btn-secondary"
                style={{ width: '100%', fontSize: '0.82rem' }}
                onClick={() => setPinSetupOpen(false)}
              >
                Cancel PIN Setup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SUB-MODAL: PATTERN SETUP ─── */}
      {patternSetupOpen && (
        <div className="card-drawer-wrapper" role="presentation" data-hide-dock="true" style={{ zIndex: 100100 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setPatternSetupOpen(false)} aria-hidden="true" />
          <div
            className="modal-card glass-panel animate-scale-up"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '340px', width: '90%', margin: 'auto', borderRadius: '20px', padding: '24px', textAlign: 'center', zIndex: 100102 }}
          >
            <PatternLockCanvas
              title={patternSetupStep === 1 ? 'Draw New Pattern' : 'Confirm Your Pattern'}
              subtitle={patternSetupStep === 1 ? 'Connect at least 4 dots' : 'Draw the pattern again to confirm'}
              error={patternError}
              onComplete={handlePatternInput}
            />
            <div style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="btn-secondary"
                style={{ width: '100%', fontSize: '0.82rem' }}
                onClick={() => setPatternSetupOpen(false)}
              >
                Cancel Pattern Setup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
